// REACT IMPORTS
import React, { useEffect } from "react";
import { useMediaQuery } from "react-responsive";

// COMPONENT IMPORTS
import {
  attachmentListGet,
  useAttachmentList,
} from "store/slices/documentsSlice";
import { useUserData } from "store/slices/authSlice";
import { useShowWarn } from "store/slices/commonSlice";
import { useAppDispatch } from "store/hooks";
import EmptyDocumentsState from "components/documents/EmptyDocumentsState";
import DesktopDocumentsTable from "components/documents/DesktopDocumentsTable";
import MobileDocumentsTable from "components/documents/MobileDocumentsTable";
import PreviewDrawer from "components/documents/PreviewDrawer";
import Header from "components/layout/header";
import MessageToast from "components/ui-component/MessageToast";

// HOOKS
import { useDocumentActions } from "hooks/documents";

// CONSTANTS & TYPES
import {
  MOBILE_BREAKPOINT,
  PAGE_TITLE,
  DOCUMENTS_PAGE_SIZE,
} from "constants/documents";
import { DocumentAttachment } from "types/Documents";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";

/* ------------------------------------- DOCUMENTS ------------------------------------- */

const DocumentsContent = () => {
  const dispatch = useAppDispatch();
  const testAttrs = useTestAttrsFactory();
  const attachmentList = useAttachmentList();
  const showWarn = useShowWarn();
  const userData = useUserData();
  const isMobile = useMediaQuery({ query: MOBILE_BREAKPOINT });

  // Use custom hooks for state management
  const {
    isTableLoading,
    setIsTableLoading,
    toastMessage,
    setToastMessage,
    isDownloading,
    handleDownload,
    clearToast,
    isPreview,
    previewData,
    previewId,
    openPreview,
    closePreview,
    setPreviewData,
    setPreviewId,
    filter,
    handleNextPage,
    handlePreviousPage,
  } = useDocumentActions();

  // Fetch attachment list
  const fetchAttachmentList = async () => {
    setIsTableLoading(true);
    try {
      const payload = {
        id: userData?.data?.accountData?.id,
        ...filter,
      };
      await dispatch(attachmentListGet(payload)).unwrap();
    } catch (error: any) {
      console.error("attachmentList fetch error:", error?.message);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.data?.accountData?.id) {
      fetchAttachmentList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Handle preview opening
  const handlePreview = (document: DocumentAttachment, id?: string) => {
    openPreview(document, id);
    setToastMessage({
      variant: "",
      content: "",
      isFromPreview: false,
    });
  };

  // Handle next page
  const handleTableNextClick = () => {
    handleNextPage();
  };

  // Handle previous page
  const handleTablePreviousClick = () => {
    handlePreviousPage();
  };

  // Calculate current page
  const currentPage = filter.offset / DOCUMENTS_PAGE_SIZE + 1;

  // Empty state
  if (attachmentList?.count === 0) {
    return <EmptyDocumentsState showWarn={showWarn} />;
  }

  return (
    <div
      className="w-full h-full lg:block flex flex-col"
      {...testAttrs("page", "Documents")}
    >
      <Header
        headerContent={
          <div
            className="w-full h-fit lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5"
            {...testAttrs("title")}
          >
            {PAGE_TITLE}
          </div>
        }
      />
      <div
        className="lg:pt-[93px] pb-12 lg:h-full flex-1 min-h-0 overflow-y-auto"
        {...testAttrs("content", "Documents content")}
      >
        {/* Desktop Table */}
        {!isMobile && (
          <DesktopDocumentsTable
            data={attachmentList?.data || []}
            isTableLoading={isTableLoading}
            isDownloading={isDownloading}
            timezone={userData?.data?.accountData?.timezone}
            totalCount={attachmentList?.count || 0}
            perPage={DOCUMENTS_PAGE_SIZE}
            currentPage={currentPage}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onNextClick={handleTableNextClick}
            onPreviousClick={handleTablePreviousClick}
            onRowClick={handlePreview}
          />
        )}

        {/* Mobile Table */}
        {isMobile && (
          <MobileDocumentsTable
            data={attachmentList?.data || []}
            isTableLoading={isTableLoading}
            isDownloading={isDownloading}
            timezone={userData?.data?.accountData?.timezone}
            totalCount={attachmentList?.count || 0}
            perPage={DOCUMENTS_PAGE_SIZE}
            currentPage={currentPage}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onNextClick={handleTableNextClick}
            onPreviousClick={handleTablePreviousClick}
          />
        )}
      </div>

      {/* Preview Drawer */}
      <PreviewDrawer
        isOpen={isPreview}
        data={previewData}
        previewId={previewId}
        isMobile={isMobile}
        onClose={() => {
          closePreview();
        }}
        onDataChange={setPreviewData}
        onIdChange={setPreviewId}
        onToastMessage={setToastMessage}
      />

      {/* Toast Message */}
      <MessageToast
        key={toastMessage.nonce || "documents-toast"}
        variant={toastMessage.variant}
        content={toastMessage.content}
        isProgressBar
        width="max-w-[810px] px-5 w-full"
        className={
          toastMessage.isFromPreview
            ? "!right-5 "
            : "!right-1/2 !translate-x-1/2 "
        }
        onCloseClick={clearToast}
        {...testAttrs("toast", "Documents notification")}
      />
    </div>
  );
};

const Documents = () => (
  <TestIdScope scope="documents">
    <DocumentsContent />
  </TestIdScope>
);

export default Documents;
